<template>
    <div class="flex flex-col min-h-screen">
        <notifications group="auth" />
        <notifications group="api" />
        <HeaderTopBarDesktop />
        <Loading v-if="$store.getters.isFetchingCustomerInfo" />
        <router-view />
        <footer class="flex-grow flex">
            <FooterComponent />
        </footer>
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import FooterComponent from '@/components/FooterComponent.vue';
import HeaderTopBarDesktop from '@/components/HeaderTopBarDesktop.vue';
import DashboardView from '@/views/DashboardView.vue';
import InvoiceReport from '@/views/Reports/InvoiceReport.vue';
import store from '@/store';
import i18n from '@/i18n';
import router from '@/router';
import Loading from '@/components/Loading.vue';

@Component({
    components: {
        Loading,
        InvoiceReport,
        DashboardView,
        HeaderTopBarDesktop,
        FooterComponent
    },
    metaInfo: {
        title: i18n.t('nav.top.self_service') as string,
        link: [
            {
                rel: 'preconnect',
                href: 'https://fonts.googleapis.com'
            },
            {
                rel: 'preconnect',
                href: 'https://fonts.gstatic.com'
            },
            {
                rel: 'stylesheet',
                href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap'
            },
            {
                rel: 'stylesheet',
                href: 'https://use.typekit.net/hpk6tfe.css'
            },
            {
                rel: 'icon',
                href: require('@/assets/static/images/estravel-symbol.png')
            }
        ],
        meta: [
            { charset: 'utf-8' },
            {
                name: 'viewport',
                content: 'width=device-width, initial-scale=1'
            },
            {
                name: 'robots',
                content: 'noindex,nofollow'
            }
        ]
    }
})
export default class App extends Vue {
    lastScrollY = window.scrollY;
    isScrollingUp = false;

    mounted() {
        // Scrolling
        window.addEventListener('scroll', this.handleScroll);
        document.addEventListener('DOMContentLoaded', this.handleActiveItemInView);
        // Scroll to the top of the page when the component is mounted
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });

        // Scroll to the top of the page when a navigation event occurs
        this.$router.beforeEach((to, from, next) => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            next();
        });
    }

    updated() {
        this.checkTokenValidity();
    }

    beforeDestroy() {
        window.removeEventListener('scroll', this.handleScroll);
        document.removeEventListener('DOMContentLoaded', this.handleActiveItemInView);
    }

    handleScroll() {
        const scrolled = window.scrollY;
        if (scrolled > 0) {
            document.body.classList.add('scrolled');
            this.isScrollingUp = scrolled < this.lastScrollY;
            if (this.isScrollingUp) {
                document.body.classList.add('scrolled-up');
            } else {
                document.body.classList.remove('scrolled-up');
            }
        } else {
            document.body.classList.remove('scrolled');
            document.body.classList.remove('scrolled-up');
        }
        this.lastScrollY = scrolled;
    }

    handleActiveItemInView() {
        const menu = document.querySelector('.self-main-nav');
        if (!menu) return; // Exit early if menu is null

        const menuParent = menu.parentElement;
        if (!menuParent) return; // Exit early if menuParent is null

        const activeItem = menu.querySelector('.self-main-nav-item.active');
        if (!activeItem) return; // Exit early if activeItem is null

        if (activeItem && menuParent) {
            // Calculate if the active item is in view considering the parent's scroll position
            const parentRect = menuParent.getBoundingClientRect();
            const itemRect = activeItem.getBoundingClientRect();

            // Check visibility relative to the parent
            if (itemRect.right > parentRect.right || itemRect.left < parentRect.left) {
                // Adjust scroll position of the parent to bring the active item into view
                // Calculate the scroll amount needed to bring the item into view
                const scrollLeftNeeded = itemRect.left - parentRect.left + menuParent.scrollLeft;
                // Smoothly scroll the parent container
                menuParent.scrollTo({
                    left: scrollLeftNeeded,
                    behavior: 'smooth'
                });
            }
        }
    }

    checkTokenValidity() {
        if (store.state.token && store.state.expiresAt) {
            const expiresAt = parseInt(store.state.expiresAt);
            const currentTime = Date.now();
            if (currentTime >= expiresAt * 1000) {
                // Token has expired
                this.redirectToHomePageWithErrorMessage();
            }
        }
    }

    async redirectToHomePageWithErrorMessage() {
        // Redirect to the home page with an error message
        await store.dispatch('clear');
        if (router.currentRoute.name !== 'AccountLogin') {
            Vue.notify({
                group: 'auth',
                title: i18n.t('notifications.session_expired.title') as string,
                text: i18n.t('notifications.session_expired.text') as string,
                type: 'warn'
            });
            await router.push({ name: 'AccountLogin' });
        }
    }
}
</script>
